import {
  type Account,
  addAccountsToPortfolio,
  ChainType,
  type ConnectedWallet,
  makeNewWatchAddressAccount,
  removeAccountsFromPortfolio,
  type SessionKeyData,
  type WalletAccount,
  type WalletGroupedAccounts,
  walletGroupedAccounts,
} from '@polaris/core/models'
import { cleanupAddressEventSource } from '@polaris/core/services'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback } from 'react'
import type { Ampli } from '@/src/codegen/ampli'
import { errorToast, keplrInvalidStateToast } from '@/src/components/ui/toast'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import { userAccountsQuery } from '@/src/queries/keys/user'
import {
  deleteAccounts,
  setAccounts as setAccountsService,
} from '@/src/services/user'
import { commitAccounts as commitAccountsService } from '@/src/services/window-user'
import { userAtom } from '../user/atoms'
import { accountsAtom, isLocalStateModeAtom, localAccountsAtom } from './atoms'
import { MAX_ACCOUNTS } from './constants'
import { pendingAccountsAtom, walletAccountsAtom } from './wallets'

export const useAccountsActions = () => {
  const user = useAtomValue(userAtom)
  const queryClient = useQueryClient()
  const { accountsWithWallets, ignoredAccountsWithWallets } =
    useAtomValue(walletAccountsAtom)
  const { accountsWithWallets: pendingAccountsWithWallets } =
    useAtomValue(pendingAccountsAtom)
  const setLocalAccounts = useSetAtom(localAccountsAtom)
  const accounts = useAtomValue(accountsAtom)
  const isLocalState = useAtomValue(isLocalStateModeAtom)
  const { ampli } = useAmplitudeAnalytics()

  const addAccounts = useMutation({
    mutationKey: ['add-accounts'],
    mutationFn: async (newAccounts: Account[]) => {
      if (!accounts) {
        console.warn(
          'No accounts state found when adding accounts, skipping add-accounts mutation',
        )
        return
      }

      const updatedAccountsState = addAccountsToPortfolio(newAccounts, accounts)

      if (
        !isValidAccountsState(
          updatedAccountsState,
          accountsWithWallets.concat(pendingAccountsWithWallets),
          ampli,
        )
      ) {
        return
      }

      if (isLocalState) {
        setLocalAccounts(updatedAccountsState)
      } else {
        await setAccountsService(user, updatedAccountsState)
      }
    },
    onSuccess: () => {
      if (isLocalState) return
      queryClient.invalidateQueries(userAccountsQuery())
    },
  })

  const addWalletGroupedAccounts = useCallback(
    (
      groupedAccounts: WalletGroupedAccounts,
      status: 'linked' | 'ignored' = 'linked',
    ) => {
      const accountsToAdd = pendingAccountsWithWallets
        .concat(accountsWithWallets)
        .concat(ignoredAccountsWithWallets)
        .filter(
          (account) =>
            groupedAccounts.accounts.some(
              (groupedAccount) => groupedAccount.address === account.address,
            ) && account.type === 'BrowserExtension',
        )
        .map<Account>((account) => ({
          ...account,
          // Set account status from user input
          account_status: status,
        }))

      return addAccounts.mutateAsync(accountsToAdd)
    },
    [
      addAccounts,
      pendingAccountsWithWallets,
      accountsWithWallets,
      ignoredAccountsWithWallets,
    ],
  )

  // From new user or guest > email user who's accounts sync with DB.
  const commitAccounts = useMutation({
    mutationKey: ['commit-accounts'],
    mutationFn: async ({
      accessToken,
      sessionKeyData,
    }: {
      accessToken: string
      sessionKeyData?: SessionKeyData
    }) =>
      commitAccountsService(accounts, accessToken, sessionKeyData, queryClient),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: userAccountsQuery().queryKey,
      })
      // Clean up local accounts state, it could potentially be large with wallet images involved.
      // Recall- this mutation will throw if in guest mode.
      setLocalAccounts([])
    },
  })

  const removeAccounts = useMutation({
    mutationKey: ['remove-accounts'],
    mutationFn: async (
      accountsToRemove: (Pick<Account, 'id' | 'address' | 'ecosystem'> & {
        connectedWallet?: ConnectedWallet
      })[],
    ) => {
      if (!accounts) {
        console.warn(
          'No accounts state found when removing accounts, skipping remove-accounts mutation',
        )
        return
      }

      const updatedAccountsState = removeAccountsFromPortfolio(
        accountsToRemove,
        accounts,
      )

      // Clean up balances cache and streams for removed addresses
      const removedAddresses = accountsToRemove.map((a) => a.address)
      for (const address of removedAddresses) {
        // Remove query cache to prevent stale data
        queryClient.removeQueries({
          queryKey: ['balances', 'stream', address],
        })
        // Close EventSource and remove from cache
        cleanupAddressEventSource(address)
      }

      if (isLocalState) {
        setLocalAccounts(updatedAccountsState)
      } else {
        const ids = accountsToRemove.map((a) => a.id!)

        if (ids.some((id) => !id)) {
          console.error(
            'Some accounts to remove have no id, skipping deleteAccounts mutation',
          )
          return
        }

        await deleteAccounts(user, ids)
      }
      return updatedAccountsState
    },
    onSuccess: () => {
      if (isLocalState) return
      queryClient.invalidateQueries(userAccountsQuery())
    },
    onMutate: () => {
      // Reset mutation to allow other accounts to be deleted after some time.
      setTimeout(() => {
        removeAccounts.reset()
      }, 4_000)
    },
  })

  const addWatchAddress = useCallback(
    async (address: string, chainType: ChainType, name?: string) => {
      const account = makeNewWatchAddressAccount(
        address,
        chainType,
        name,
        'Desktop',
      )
      await addAccounts.mutateAsync([account])
    },
    [addAccounts],
  )

  return {
    addAccounts,
    addWalletGroupedAccounts,
    /**
     * Special action used exclusively for setting local accounts state to BE
     * and turning on sync mode.
     *
     * Useful for onboarding.
     */
    commitAccounts,
    removeAccounts,
    addWatchAddress,
  }
}

/**
 * Validates the next accounts state after an add operation.
 *
 * Rarely, Keplr may fail to update the EVM connection when switching accounts while Cosmos/Bitcoin update,
 * causing disparate accounts to share the prior `grouping_key`. When a Keplr provider grouping has more than
 * three accounts, we infer this invalid state and prompt the user to refresh to reset wallet connections.
 * This issue is very hard to repro and Keplr team has been notified, so it shouldn't happen often. The goal is
 * to prevent those unlucky users from creating invalid groupings and to assist them with fixing it.
 *
 * Also enforces a global MAX_ACCOUNTS limit.
 */
export function isValidAccountsState(
  updatedAccountsState: Account[],
  accountsWithWallets: (Account | WalletAccount)[],
  ampli: Ampli | undefined = undefined,
): boolean {
  if (updatedAccountsState.length > MAX_ACCOUNTS) {
    errorToast(`Up to ${MAX_ACCOUNTS} accounts can be added`)
    return false
  }

  const grouped = walletGroupedAccounts(updatedAccountsState)
  const invalidGroup = grouped.find((group) => {
    if (group.provider_info?.name !== 'Keplr') {
      return false
    }

    // For Bitcoin accounts, filter them since we no longer support bitcoin Keplr connectors
    // Context https://linear.app/osmosis/issue/POLFE-1121/remove-bitcoin-connector
    const filteredAccounts = group.accounts.filter(
      (account) => account.ecosystem !== ChainType.Bitcoin,
    )

    // Keplr only supports 2 connectors
    return filteredAccounts.length > 2
  })

  if (invalidGroup) {
    // Find the connected wallet for this group
    const connectedWallet = accountsWithWallets.find((account) =>
      invalidGroup.accounts.some((a) => a.address === account.address),
    )

    const walletAccount =
      connectedWallet && 'wallet' in connectedWallet
        ? (connectedWallet as WalletAccount)
        : undefined

    ampli?.walletsKeplrInvalidState()

    keplrInvalidStateToast(walletAccount?.wallet)
    return false
  }

  return true
}
